import { Link } from "gatsby";
import React, { useState, useEffect } from "react";
import { Nav } from "./navbar";
import CartSidebar from "../cart/cartSidebar";
import SearchInput from "./searchInput";
import { useMenu } from "../../hooks/useMenu";
import { format, addDays } from "date-fns";
import Container from "./container";
import { __t } from "../../utils/translate";
import { Data } from "@/utils/optionsTypes";
import { useOptions } from "@/hooks/useOptions";
import { BurgerMenuToggle } from "./burgerMenu";
import SearchModal from "./searchModal";
import { useStore } from "@nanostores/react";
import { $limdisc } from "@/utils/store";

const Header: React.FC<React.PropsWithChildren> = ({ options, hiddenHeader = false }) => {

    const header_menu = useMenu('header');

    if (!header_menu || !header_menu.items) {
        return null;
    }

    const categories = header_menu.items;
    const limdisc = useStore($limdisc);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSaleClosed, setIsSaleClosed] = useState(false);

    useEffect(() => {
        setIsSaleClosed(sessionStorage.getItem('offerClosed') !== null);
    }, []);

    return (
        <>
            {!isSaleClosed && !limdisc && (
                <SaleWarning
                    setIsSaleClosed={setIsSaleClosed}
                    className=""
                    options={options}
                />
            )}
            {!hiddenHeader &&
                <header
                    className={`z-50`}
                >
                    <Container
                        className='!max-w-[1250px] box-border mx-auto md:shadow-header md:rounded-b-2xl !px-[10px] lg:!px-10 py-[10px] md:mb-5 lg:mb-6 bg-white'
                    >
                        <div className="w-full flex flex-row items-center justify-between relative">
                            <div className="lg:hidden">
                                {/* {isMenuOpen && (
                                    <BurgerMenuClose
                                        className="self-end"
                                        setIsOpen={() => setIsMenuOpen(false)}
                                    />
                                ) || (
                                )} */}
                                <BurgerMenuToggle
                                    className={`self-center`}
                                    setIsOpen={setIsMenuOpen}
                                    isMenuOpen={isMenuOpen}
                                />
                            </div>
                            <Link aria-label="Home page" to={`/`}>
                                <Logo
                                    className="w-[134px] lg:ml-[10px]"
                                />
                            </Link>
                            <div className="lg:flex hidden flex-row grow items-center justify-center">
                                <SearchInput
                                    className="ml-20 mr-14 max-w-[570px]" />
                                <div className="min-w-[233px] block bg-transparent">
                                    {options.phone && (
                                        <a className="whitespace-nowrap px-2" title={options.phone_title} href={`tel:${options.phone}`}>{options.phone}</a>
                                    ) || (
                                            <>&nbsp;</>
                                        )}
                                </div>
                            </div>
                            <div className="absolute right-1 lg:hidden">
                                <SearchModal />
                            </div>
                            <CartSidebar
                                options={options}
                            />
                        </div>
                    </Container>
                    <Nav
                        className={`w-full flex flex-col justify-start items-start overflow-y-auto`}
                        showNav={isMenuOpen}
                        isMenuOpen={isMenuOpen}
                        setIsOpen={setIsMenuOpen}
                        categories={categories}
                    />
                </header>
            }
        </>
    );
};

type SaleWarningProps = {
    className?: string;
    options: Data;
    setIsSaleClosed: (isOpen: boolean) => void;
};

const SaleWarning: React.FC<SaleWarningProps> = ({
    className = '',
    setIsSaleClosed,
}) => {
    const options = useOptions().wpOptions.data;
    const [saleEnds, setSaleEnds] = React.useState(options.sale_end_date + ".");

    useEffect(() => {
        const saleEndsDate = format(addDays(new Date(), 2), "d MMM") + ".";
        if (saleEnds !== saleEndsDate) {
            setSaleEnds(saleEndsDate);
        }
    }, []);

    // const [saleEnds, setSaleEnds] = React.useState(format(new Date(2024, 11, 2), "d MMM"));

    const onClick = () => {
        setIsSaleClosed(true);
        sessionStorage.setItem('offerClosed', 'true');
    };

    return (
        <div className={`${className} h-fit min-h-[26px] w-full relative flex flex-row bg-dark-blu items-center justify-center max-w-[2170px] mx-auto`}>

            <p className="max-w-[90%] font-plex text-white text-sm sm:text-small-14 md:text-base-16 !leading-[18px] tracking-normal md:!leading-6 text-center py-1 md:py-0 ml-1 md:ml-3 whitespace-normal !font-semibold">
                ⏰{__t("Sale Ends")} {saleEnds} {__t("20-50% OFF + Free Shipping")}
            </p>

            <button
                aria-label="Sale close"
                className="absolute top-1 right-1"
                onClick={onClick}>
                <svg className="w-[10px] h-[10px] m-1" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" version="1.1">
                    <path fill="#F5F8FB" stroke="#F5F8FB" strokeWidth="4" d="M 20,4 3,21 33,50 3,80 20,97 49,67 79,97 95,80 65,50 95,20 80,4 50,34 z" />
                </svg>
            </button>
        </div>
    );
};

type LogoProps = {
    className?: string;
};

export const Logo: React.FC<LogoProps> = ({ className = "" }) => {
    return (
        <svg className={className} width="168" height="32" viewBox="0 0 168 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5762 4.99414H12.1871C7.07346 4.99414 2.92773 9.13987 2.92773 14.2535V17.5041C2.92773 22.6177 7.07346 26.7634 12.1871 26.7634H16.5762C21.6898 26.7634 25.8355 22.6177 25.8355 17.5041V14.2535C25.8355 9.13987 21.6898 4.99414 16.5762 4.99414ZM21.6507 17.8985C21.6507 20.7966 19.3008 23.1464 16.4028 23.1464H12.2906C9.3925 23.1464 7.04269 20.7966 7.04269 17.8985V13.8563C7.04269 10.9582 9.3925 8.60837 12.2906 8.60837H16.4028C19.3008 8.60837 21.6507 10.9582 21.6507 13.8563V17.8985Z" fill="#194567" stroke="#194567" strokeWidth="0.279739" strokeMiterlimit="10" />
            <path d="M83.94 4.99414H79.5509C74.4372 4.99414 70.2915 9.13987 70.2915 14.2535V17.5041C70.2915 22.6177 74.4372 26.7634 79.5509 26.7634H83.94C89.0536 26.7634 93.1993 22.6177 93.1993 17.5041V14.2535C93.1993 9.13987 89.0536 4.99414 83.94 4.99414ZM89.0144 17.8985C89.0144 20.7966 86.6646 23.1464 83.7665 23.1464H79.6544C76.7563 23.1464 74.4065 20.7966 74.4065 17.8985V13.8563C74.4065 10.9582 76.7563 8.60837 79.6544 8.60837H83.7665C86.6646 8.60837 89.0144 10.9582 89.0144 13.8563V17.8985Z" fill="#194567" stroke="#194567" strokeWidth="0.279739" strokeMiterlimit="10" />
            <path d="M28.3896 26.4807V7.79696C28.3896 6.4626 29.4694 5.38281 30.8038 5.38281H45.1152C47.493 5.38281 49.4204 7.31021 49.4204 9.68799V12.9302C49.4204 16.6003 46.444 19.5768 42.7738 19.5768H42.6647L50.6233 26.4835H45.5209C44.7404 26.4835 43.9879 26.1982 43.406 25.6835L36.4741 19.5656H33.7718V16.0213H42.9584C44.5865 16.0213 45.9097 14.7009 45.9097 13.07V10.6531C45.9097 9.895 45.2971 9.28237 44.539 9.28237H32.2444V26.4807H28.3896Z" fill="#194567" stroke="#194567" strokeWidth="0.279739" strokeMiterlimit="10" />
            <path d="M95.7563 26.5002V7.79403C95.7563 6.45967 96.8361 5.37988 98.1705 5.37988H112.482C114.86 5.37988 116.787 7.30728 116.787 9.68506V12.9776C116.787 16.6478 113.811 19.6242 110.141 19.6242H110.031L117.937 26.4862H112.834C112.054 26.4862 111.301 26.2008 110.717 25.6861L103.841 19.613H101.139V16.0715H110.325C111.953 16.0715 113.276 14.7511 113.276 13.1203V10.653C113.276 9.89487 112.664 9.28224 111.906 9.28224H99.6111V26.4806L95.7563 26.5002Z" fill="#194567" stroke="#194567" strokeWidth="0.279739" strokeMiterlimit="10" />
            <path d="M71.0722 5.38277H62.6101V5.37158H58.9763V5.38277H50.5142V5.80797C50.5142 7.57872 51.9492 9.01658 53.7228 9.01658H58.9791V26.4779H62.6129V9.01658H67.8692C69.6399 9.01658 71.0778 7.58152 71.0778 5.80797V5.38277H71.0722Z" fill="#194567" stroke="#194567" strokeWidth="0.279739" strokeMiterlimit="10" />
            <path d="M134.685 5.36621H121.207C120.18 5.36621 119.347 6.19983 119.347 7.22647V21.4708C119.347 24.2402 121.593 26.4865 124.362 26.4865H134.785C136.886 26.4865 138.59 24.7829 138.59 22.682H124.2C123.45 22.682 122.841 22.0722 122.841 21.3225V9.18744H138.489V9.15108C138.478 7.05863 136.78 5.36621 134.685 5.36621Z" fill="#194567" stroke="#194567" strokeWidth="0.279739" strokeMiterlimit="10" />
            <path d="M135.233 14.1777H122.801V17.6996H135.233V14.1777Z" fill="#194567" stroke="#194567" strokeWidth="0.279739" strokeMiterlimit="10" />
            <path d="M150.798 12.9861L156.426 6.23322C156.874 5.69612 157.537 5.38561 158.236 5.38561H162.379L153.285 15.7024L162.513 26.4695H158.41C157.713 26.4695 157.05 26.1618 156.603 25.6275L150.784 18.6872L144.963 25.6247C144.515 26.159 143.852 26.4667 143.156 26.4667H139.052L148.28 15.6996L139.186 5.38281H143.329C144.028 5.38281 144.691 5.69332 145.139 6.23042L150.767 12.9833H150.798V12.9861Z" fill="#194567" stroke="#194567" strokeWidth="0.279739" strokeMiterlimit="10" />
        </svg>

    );
};

export default Header;
